import { useMemo, useState } from 'react';
import { AreaChart as AreaIcon, BarChart3, LineChart as LineIcon } from 'lucide-react';
import {
  Area,
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  XAxis,
  YAxis,
} from 'recharts';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/components/ui/chart';
import {
  buildExplorerData,
  buildPlatformLegend,
  EXPLORER_METRICS,
  METRIC_DASH,
  type ExplorerChartType,
  type ExplorerMetric,
} from '../../../lib/reporting/socialExplorer';
import { ChannelIcon } from '../../../components/ui/ChannelIcon';
import type { SocialOverview, SocialPlatform } from '../../../types/socialReporting';
import { cn } from '../../../lib/utils';

// Organic Social Reporting — the Overview trend EXPLORER. A multi-metric ×
// multi-platform ComposedChart: tick the metrics (each with a line/bar/area
// control), toggle platforms in the legend. One line per ACTIVE metric × ACTIVE
// platform, but only where a daily series exists — the availability matrix
// (socialExplorer.ts) greys the rest with a reason instead of drawing a fake zero.
//
// Colour = platform; line STYLE = metric (so same-colour lines stay readable).
// Posts published → bars on the right axis; continuous metrics → the left axis.

const PLATFORM_ORDER: SocialPlatform[] = ['facebook', 'instagram', 'googlebusiness'];
const CHART_TYPE_ICON: Record<ExplorerChartType, typeof LineIcon> = {
  line: LineIcon,
  bar: BarChart3,
  area: AreaIcon,
};

function labelDate(ymd: string): string {
  const d = new Date(`${ymd}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return ymd;
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', timeZone: 'UTC' });
}

export function SocialTrendExplorer({ data }: { data: SocialOverview }) {
  const connectedPlatforms = PLATFORM_ORDER.filter((p) => data.connected[p]);

  // Default: followers (the growth anchor) + posts (universal — so a GBP-only
  // client still sees cadence). Users tick reach / engagement to add lines.
  const [activeMetrics, setActiveMetrics] = useState<Set<ExplorerMetric>>(
    () => new Set<ExplorerMetric>(['followers', 'posts']),
  );
  const [activePlatforms, setActivePlatforms] = useState<Set<SocialPlatform>>(
    () => new Set(connectedPlatforms),
  );
  const [chartTypes, setChartTypes] = useState<Record<ExplorerMetric, ExplorerChartType>>(() =>
    Object.fromEntries(EXPLORER_METRICS.map((m) => [m.key, m.defaultChart])) as Record<
      ExplorerMetric,
      ExplorerChartType
    >,
  );

  const explorer = useMemo(
    () =>
      buildExplorerData(data, {
        activeMetrics: [...activeMetrics],
        activePlatforms: [...activePlatforms],
        chartTypes,
      }),
    [data, activeMetrics, activePlatforms, chartTypes],
  );

  const legend = useMemo(
    () => buildPlatformLegend(data, [...activeMetrics]),
    [data, activeMetrics],
  );

  const config: ChartConfig = useMemo(
    () => Object.fromEntries(explorer.series.map((s) => [s.key, { label: s.label, color: s.color }])),
    [explorer.series],
  );

  const toggleMetric = (m: ExplorerMetric) =>
    setActiveMetrics((cur) => {
      const next = new Set(cur);
      next.has(m) ? next.delete(m) : next.add(m);
      return next;
    });
  const setChartType = (m: ExplorerMetric, t: ExplorerChartType) =>
    setChartTypes((cur) => ({ ...cur, [m]: t }));
  const togglePlatform = (p: SocialPlatform) =>
    setActivePlatforms((cur) => {
      const next = new Set(cur);
      next.has(p) ? next.delete(p) : next.add(p);
      return next;
    });

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-medium text-slate-700">Trends</h3>
      </div>

      {/* Metric ticks + per-metric chart-type control. */}
      <div className="mb-3 flex flex-wrap gap-2">
        {EXPLORER_METRICS.map((m) => {
          const active = activeMetrics.has(m.key);
          return (
            <div
              key={m.key}
              className={cn(
                'flex items-center gap-2 rounded-md border px-2 py-1',
                active ? 'border-slate-300 bg-slate-50' : 'border-slate-200 bg-white',
              )}
            >
              <button
                type="button"
                onClick={() => toggleMetric(m.key)}
                className="flex items-center gap-1.5 text-sm"
              >
                <span
                  className={cn(
                    'flex h-4 w-4 items-center justify-center rounded border',
                    active ? 'border-kyck-500 bg-kyck-500 text-white' : 'border-slate-300 text-transparent',
                  )}
                  aria-hidden
                >
                  <svg viewBox="0 0 10 10" className="h-3 w-3">
                    <path d="M2 5l2 2 4-4" fill="none" stroke="currentColor" strokeWidth="1.5" />
                  </svg>
                </span>
                <MetricStyleGlyph metric={m.key} />
                <span className={active ? 'font-medium text-slate-800' : 'text-slate-600'}>{m.label}</span>
              </button>
              {active && (
                <div className="flex items-center gap-0.5 rounded border border-slate-200 bg-white p-0.5">
                  {(['line', 'bar', 'area'] as ExplorerChartType[]).map((t) => {
                    const Icon = CHART_TYPE_ICON[t];
                    return (
                      <button
                        key={t}
                        type="button"
                        title={`${m.label}: ${t}`}
                        onClick={() => setChartType(m.key, t)}
                        className={cn(
                          'rounded p-1',
                          chartTypes[m.key] === t
                            ? 'bg-kyck-50 text-kyck-700'
                            : 'text-slate-400 hover:text-slate-600',
                        )}
                      >
                        <Icon className="h-3.5 w-3.5" aria-hidden />
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Chart. */}
      {explorer.series.length === 0 ? (
        <div className="flex h-72 items-center justify-center text-sm text-slate-400">
          Pick a metric and a platform with data to plot.
        </div>
      ) : (
        <ChartContainer config={config} className="aspect-auto h-72 w-full">
          <ComposedChart data={explorer.rows} margin={{ left: 4, right: 8, top: 4, bottom: 0 }} accessibilityLayer>
            <CartesianGrid vertical={false} strokeDasharray="3 3" />
            <XAxis
              dataKey="date"
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              minTickGap={28}
              tickFormatter={labelDate}
            />
            <YAxis yAxisId="left" hide domain={['auto', 'auto']} />
            {explorer.hasRightAxis && (
              <YAxis yAxisId="right" orientation="right" width={28} allowDecimals={false} tickLine={false} axisLine={false} />
            )}
            <ChartTooltip
              content={
                <ChartTooltipContent
                  labelFormatter={(v) => labelDate(String(v))}
                  formatter={(value, name) => (
                    <span className="flex w-full items-center justify-between gap-4">
                      <span className="text-muted-foreground">{name}</span>
                      <span className="font-mono font-medium tabular-nums text-foreground">
                        {value === null || value === undefined ? '—' : Number(value).toLocaleString()}
                      </span>
                    </span>
                  )}
                />
              }
            />
            {explorer.series.map((s) => {
              if (s.chartType === 'bar') {
                return (
                  <Bar
                    key={s.key}
                    yAxisId={s.axis}
                    dataKey={s.key}
                    name={s.label}
                    fill={s.color}
                    radius={[2, 2, 0, 0]}
                    maxBarSize={22}
                    isAnimationActive={false}
                  />
                );
              }
              if (s.chartType === 'area') {
                return (
                  <Area
                    key={s.key}
                    yAxisId={s.axis}
                    type="monotone"
                    dataKey={s.key}
                    name={s.label}
                    stroke={s.color}
                    fill={s.color}
                    fillOpacity={0.15}
                    strokeWidth={2}
                    strokeDasharray={s.dash}
                    dot={false}
                    connectNulls
                    isAnimationActive={false}
                  />
                );
              }
              return (
                <Line
                  key={s.key}
                  yAxisId={s.axis}
                  type="monotone"
                  dataKey={s.key}
                  name={s.label}
                  stroke={s.color}
                  strokeWidth={2}
                  strokeDasharray={s.dash}
                  dot={false}
                  connectNulls
                  isAnimationActive={false}
                />
              );
            })}
          </ComposedChart>
        </ChartContainer>
      )}

      {/* Platform legend — clickable toggles; greyed with a reason when the active
          metrics have no series for a platform (e.g. GBP without Posts published). */}
      <div className="mt-3 flex flex-wrap gap-2 border-t border-slate-100 pt-3">
        {legend.map((entry) => {
          const on = entry.enabled && activePlatforms.has(entry.platform);
          return (
            <button
              key={entry.platform}
              type="button"
              disabled={!entry.enabled}
              title={entry.reason ?? undefined}
              onClick={() => togglePlatform(entry.platform)}
              className={cn(
                'flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors',
                !entry.enabled
                  ? 'cursor-not-allowed border-slate-200 bg-slate-50 text-slate-300'
                  : on
                    ? 'border-slate-300 bg-white text-slate-800'
                    : 'border-slate-200 bg-white text-slate-400 line-through',
              )}
            >
              <span
                className="h-2.5 w-2.5 rounded-full"
                style={{ backgroundColor: entry.enabled ? entry.color : '#cbd5e1' }}
                aria-hidden
              />
              <ChannelIcon channel={entry.platform} className="h-3.5 w-3.5" />
              {entry.label}
              {!entry.enabled && entry.reason && (
                <span className="ml-1 font-normal normal-case">· {shortReason(entry.reason)}</span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// A tiny preview of the metric's line style (dash) — or a bar glyph for posts.
function MetricStyleGlyph({ metric }: { metric: ExplorerMetric }) {
  if (metric === 'posts') {
    return (
      <svg viewBox="0 0 20 10" className="h-2.5 w-5 text-slate-400" aria-hidden>
        <rect x="2" y="3" width="3" height="6" fill="currentColor" />
        <rect x="8" y="1" width="3" height="8" fill="currentColor" />
        <rect x="14" y="5" width="3" height="4" fill="currentColor" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 20 4" className="h-1.5 w-5 text-slate-400" aria-hidden>
      <line x1="0" y1="2" x2="20" y2="2" stroke="currentColor" strokeWidth="1.5" strokeDasharray={METRIC_DASH[metric]} />
    </svg>
  );
}

// Trim the full availability reason to a compact legend suffix.
function shortReason(reason: string): string {
  if (/period total/i.test(reason)) return 'no daily engagement';
  if (/no reach, engagement or follower/i.test(reason)) return 'posts only';
  return reason;
}
